/**
 * ConflictResolutionContext
 *
 * Verwaltet Sync-Konflikte und deren Auflösung
 *
 * Strategien:
 * - SERVER: Server-Version übernehmen
 * - LOCAL: Lokale Version erzwingen
 * - MERGE: Intelligentes Zusammenführen
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useOfflineQueue } from '../hooks/useOfflineQueue';

// Konflikt-Auflösungs-Strategien
export const ConflictStrategy = {
  SERVER: 'SERVER',  // Server-Version gewinnt
  LOCAL: 'LOCAL',    // Lokale Version gewinnt
  MERGE: 'MERGE'     // Zusammenführen
};

const ConflictResolutionContext = createContext(null);

export const useConflictResolution = () => {
  const context = useContext(ConflictResolutionContext);
  if (!context) {
    throw new Error('useConflictResolution muss innerhalb von ConflictResolutionProvider verwendet werden');
  }
  return context;
};

export const ConflictResolutionProvider = ({ children }) => {
  const { conflicts, resolveConflict: resolveQueueConflict, getConflictDetails } = useOfflineQueue();

  const [activeConflict, setActiveConflict] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isResolving, setIsResolving] = useState(false);

  // Öffnet den Konflikt-Dialog wenn Konflikte vorhanden sind
  useEffect(() => {
    if (conflicts.length > 0 && !activeConflict && !isModalOpen) {
      // Automatisch ersten Konflikt anzeigen
      showConflict(conflicts[0].id);
    }
  }, [conflicts, activeConflict, isModalOpen]);

  /**
   * Zeigt einen bestimmten Konflikt an
   */
  const showConflict = useCallback(async (conflictId) => {
    try {
      const details = await getConflictDetails(conflictId);
      if (details) {
        setActiveConflict(details);
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error('[ConflictResolution] Fehler beim Laden des Konflikts:', error);
    }
  }, [getConflictDetails]);

  /**
   * Schließt den Konflikt-Dialog
   */
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setActiveConflict(null);
  }, []);

  /**
   * Löst den aktiven Konflikt auf
   */
  const resolve = useCallback(async (strategy, mergedData = null) => {
    if (!activeConflict) return;

    setIsResolving(true);
    try {
      await resolveQueueConflict(activeConflict.id, strategy, mergedData);
      closeModal();

      // Wenn weitere Konflikte vorhanden, nächsten anzeigen
      const remainingConflicts = conflicts.filter(c => c.id !== activeConflict.id);
      if (remainingConflicts.length > 0) {
        setTimeout(() => showConflict(remainingConflicts[0].id), 300);
      }
    } catch (error) {
      console.error('[ConflictResolution] Fehler beim Auflösen:', error);
    } finally {
      setIsResolving(false);
    }
  }, [activeConflict, conflicts, resolveQueueConflict, closeModal, showConflict]);

  /**
   * Führt zwei Versionen intelligent zusammen
   * Behält Änderungen von beiden Seiten wo möglich
   */
  const mergeVersions = useCallback((serverVersion, localVersion) => {
    if (!serverVersion || !localVersion) return localVersion || serverVersion;

    const merged = { ...serverVersion };

    // Für jeden Key in der lokalen Version
    Object.keys(localVersion).forEach(key => {
      // System-Keys überspringen
      if (key.startsWith('_') || key === 'id' || key === 'createdAt') return;

      const serverValue = serverVersion[key];
      const localValue = localVersion[key];

      // Wenn Server-Wert nicht existiert oder null ist, lokalen nehmen
      if (serverValue === undefined || serverValue === null) {
        merged[key] = localValue;
        return;
      }

      // Wenn beide Arrays sind (z.B. items), zusammenführen
      if (Array.isArray(serverValue) && Array.isArray(localValue)) {
        merged[key] = mergeArrays(serverValue, localValue);
        return;
      }

      // Wenn beide Objekte sind, rekursiv mergen
      if (typeof serverValue === 'object' && typeof localValue === 'object') {
        merged[key] = mergeVersions(serverValue, localValue);
        return;
      }

      // Bei primitiven Werten: Server gewinnt (außer updatedAt)
      // Lokale Änderungen sollten bereits in der Queue sein
    });

    return merged;
  }, []);

  /**
   * Führt zwei Arrays zusammen
   */
  const mergeArrays = (serverArray, localArray) => {
    // Für Shopping List Items: Nach Name identifizieren
    const serverMap = new Map();
    serverArray.forEach((item, index) => {
      const key = item.name || item.id || index;
      serverMap.set(key, item);
    });

    // Lokale Items hinzufügen/überschreiben
    localArray.forEach((item, index) => {
      const key = item.name || item.id || index;
      if (!serverMap.has(key)) {
        serverMap.set(key, item);
      }
      // Bei existierenden: Server-Version behalten (hat aktuelleren Checked-Status)
    });

    return Array.from(serverMap.values());
  };

  const value = {
    // State
    activeConflict,
    isModalOpen,
    isResolving,
    conflictCount: conflicts.length,

    // Methoden
    showConflict,
    closeModal,
    resolve,
    mergeVersions,

    // Strategien
    ConflictStrategy
  };

  return (
    <ConflictResolutionContext.Provider value={value}>
      {children}
    </ConflictResolutionContext.Provider>
  );
};

export default ConflictResolutionContext;
